import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ReactComponent as VideoCallIcon } from '@/assets/icons/video-call.svg';
import chinobiAvatar from '@/assets/images/agents/chinobi.png';
import chinobiAgenticPossibilities from '@/assets/images/meet-live-ai/chinobi-agentic-possibilities.webp';
import chinobiConversationToDemo from '@/assets/images/meet-live-ai/chinobi-conversation-to-demo.webp';
import chinobiOneConversation from '@/assets/images/meet-live-ai/chinobi-one-conversation.webp';
import chinobiSpecialtyFinanceGuidance from '@/assets/images/meet-live-ai/chinobi-specialty-finance-guidance.webp';
import chinobiStrategicPerspective from '@/assets/images/meet-live-ai/chinobi-strategic-perspective.webp';
import { Button } from '@/components/ui';
import { ROUTES } from '@/constants/routes';
import { type PersonalizedCard } from '@/types/meet-live-ai';

const PERSONALIZED_CARDS: PersonalizedCard[] = [
  {
    id: 'one-conversation',
    number: '01.',
    title: 'One Conversation',
    shortTitle: 'One Conversation',
    description:
      'Talk with Chinobi about your business, challenges, and questions, and explore what the future of autonomous specialty finance could look like.',
    bg: '#EDF5F8',
    accentBorder: '#2B99C3',
    image: chinobiOneConversation,
  },
  {
    id: 'specialty-finance',
    number: '02.',
    title: 'Specialty Finance Guidance',
    shortTitle: 'Specialty Finance Guidance',
    description:
      'Navigate regulatory complexities, originations criteria, and specialized risk parameters with real-time AI guidance.',
    bg: '#EEF2FA',
    accentBorder: '#5D77B8',
    image: chinobiSpecialtyFinanceGuidance,
  },
  {
    id: 'agentic-possibilities',
    number: '03.',
    title: 'Agentic Possibilities',
    shortTitle: 'Agentic Possibilities',
    description:
      'Discover multi-agent orchestration that dynamically triages loan applications, syncs data, and automates multi-step decisioning.',
    bg: '#EBF5EC',
    accentBorder: '#48995B',
    image: chinobiAgenticPossibilities,
  },
  {
    id: 'strategic-perspective',
    number: '04.',
    title: 'Strategic Perspective',
    shortTitle: 'Strategic Perspective',
    description:
      'Get deep operational insights, pipeline forecasting, and executive telemetry tailored to your balance sheet objectives.',
    bg: '#F0EDEA',
    accentBorder: '#8A7D75',
    image: chinobiStrategicPerspective,
  },
  {
    id: 'conversation-to-demo',
    number: '05.',
    title: 'From Conversation To Demo',
    shortTitle: 'From Conversation To Demo',
    description:
      'Transition smoothly from consultation insights into a tailored sandbox with custom agent configurations and your sample workflows.',
    bg: '#F0E6D8',
    accentBorder: '#C4925A',
    image: chinobiConversationToDemo,
  },
];

type PersonalizedAnswersSectionProps = {
  chinobiCtaPath: string;
};

export default function PersonalizedAnswersSection({
  chinobiCtaPath,
}: PersonalizedAnswersSectionProps) {
  const [activeId, setActiveId] = useState<string>('one-conversation');
  const navigate = useNavigate();

  return (
    <section
      id="personalized-answers-section"
      className="w-full overflow-hidden bg-white py-16 lg:py-20"
    >
      <div className="app-container flex flex-col items-center">
        {/* Section Heading */}
        <h2 className="mb-12 text-center font-fraunces text-2xl font-normal leading-[28px] tracking-tight text-foreground sm:text-4xl sm:leading-[1.15] lg:text-[44px]">
          Get personalized answers.
          <br className="sm:hidden" /> See the possibilities.
        </h2>

        {/* Desktop Horizontal Accordion (Hidden on mobile/small screens) */}
        <div className="hidden h-[400px] w-full items-stretch justify-center gap-3.5 lg:flex">
          {PERSONALIZED_CARDS.map(card => {
            const isActive = activeId === card.id;

            return (
              <motion.div
                key={card.id}
                id={`personalized-card-${card.id}`}
                layout
                onClick={() => setActiveId(card.id)}
                onMouseEnter={() => setActiveId(card.id)}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30,
                }}
                style={{
                  height: '400px',
                  borderRadius: '8px',
                  background: card.bg,
                  boxShadow: isActive
                    ? '0px 4px 20px 5px rgba(0, 0, 0, 0.1)'
                    : '0px 0px 4px 0px rgba(252, 220, 205, 0.05)',
                  borderLeft: isActive
                    ? `6px solid ${card.accentBorder}`
                    : '6px solid transparent',
                  paddingTop: isActive ? '28px' : '28px',
                  paddingBottom: isActive ? '28px' : '28px',
                  paddingLeft: isActive ? '40px' : '20px',
                  paddingRight: isActive ? '40px' : '20px',
                }}
                className={`relative flex cursor-pointer overflow-hidden transition-colors duration-300 ${
                  isActive
                    ? 'max-w-[880px] flex-[4.8]'
                    : 'max-w-[130px] flex-[0.8]'
                }`}
              >
                {/* Background Image for Active Card */}
                {isActive && (
                  <div className="pointer-events-none absolute inset-0 z-0">
                    <img
                      src={card.image}
                      alt={card.title}
                      referrerPolicy="no-referrer"
                      className="h-full w-full object-cover object-center opacity-[0.14] mix-blend-multiply"
                    />
                    <div className="from-white/70 to-white/40 absolute inset-0 bg-gradient-to-r via-transparent" />
                  </div>
                )}

                {/* Active Card Content */}
                {isActive ? (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.25 }}
                    className="relative z-10 flex h-full w-full flex-col justify-between"
                  >
                    <div>
                      {/* Big Serif/Display Number */}
                      <span className="my-6 block font-fraunces text-4xl font-bold italic tracking-tight text-foreground lg:text-[66.86px]">
                        {card.number}
                      </span>
                    </div>

                    <div className="w-full">
                      {/* Card Title */}
                      <h3 className="mb-3 font-fraunces font-semibold text-foreground md:text-2xl lg:text-[28px] lg:leading-[40px]">
                        {card.title}
                      </h3>

                      {/* Card Description */}
                      <p className="text-sm leading-relaxed text-foreground sm:text-sm lg:text-lg">
                        {card.description}
                      </p>
                    </div>
                  </motion.div>
                ) : (
                  /* Inactive Collapsed Card Content (Vertical Text) */
                  <div className="relative z-10 flex h-full w-full flex-col items-center py-2">
                    {/* Collapsed Number */}
                    <span className="text-foreground/90 font-fraunces text-xl font-bold italic">
                      {card.number}
                    </span>

                    {/* Top-aligned vertical title */}
                    <div
                      style={{
                        writingMode: 'vertical-rl',
                      }}
                      className="mt-4 whitespace-nowrap font-fraunces text-sm font-normal tracking-[0.3px] text-foreground opacity-90 lg:text-lg"
                    >
                      {card.shortTitle}
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Tablet & Mobile Stack View (Hidden on large desktop) */}
        <div className="flex w-full max-w-[580px] flex-col gap-3 lg:hidden">
          {PERSONALIZED_CARDS.map(card => {
            const isActive = activeId === card.id;

            return (
              <motion.div
                key={card.id}
                id={`personalized-mobile-${card.id}`}
                layout
                onClick={() => setActiveId(card.id)}
                style={{
                  background: card.bg,
                  borderRadius: '6px',
                  borderLeft: isActive
                    ? `4px solid ${card.accentBorder}`
                    : '4px solid transparent',
                  boxShadow: isActive
                    ? '0px 4px 18px rgba(0, 0, 0, 0.08)'
                    : '0px 0px 4px 0px rgba(252, 220, 205, 0.05)',
                }}
                className={`relative cursor-pointer overflow-hidden transition-all duration-300 ${
                  isActive
                    ? 'h-[224px] px-2 py-3 sm:h-[320px] sm:p-8'
                    : 'flex h-[45px] items-center px-2 sm:h-auto sm:px-5 sm:py-4'
                }`}
              >
                {isActive ? (
                  <>
                    {/* Background faint avatar */}
                    <div className="pointer-events-none absolute inset-0 z-0">
                      <img
                        src={card.image}
                        alt={card.title}
                        referrerPolicy="no-referrer"
                        className="h-full w-full object-cover opacity-[0.08] mix-blend-multiply"
                      />
                      <div className="from-white/80 via-white/30 to-white/60 absolute inset-0 bg-gradient-to-r" />
                    </div>

                    <div className="relative z-10 flex h-full flex-col justify-between">
                      <span className="block font-fraunces text-2xl font-bold italic leading-7 tracking-tight text-foreground sm:text-4xl">
                        {card.number}
                      </span>

                      <div>
                        <h3 className="mb-1 font-fraunces text-sm font-semibold leading-5 text-foreground sm:mb-2 sm:text-2xl sm:font-normal sm:leading-tight">
                          {card.title}
                        </h3>
                        <p className="text-sm leading-[22px] text-neutral-700 sm:leading-relaxed">
                          {card.description}
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <span className="font-fraunces text-xs font-bold italic text-foreground sm:text-lg">
                      {card.number}
                    </span>
                    <span className="font-fraunces text-xs font-normal text-neutral-800 sm:text-base">
                      {card.shortTitle}
                    </span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* CTA Section below cards */}
        <div className="mt-9 flex w-full max-w-[580px] flex-col items-center justify-center gap-8 sm:mt-12 sm:w-auto sm:flex-row sm:gap-8">
          {/* Speak with Chinobi button */}
          <Button
            id="speak-with-chinobi-btn"
            size={null}
            className="flex h-[56px] w-full cursor-pointer items-center gap-2 rounded-full px-6 text-xs font-medium text-white active:scale-95 sm:w-auto sm:text-base md:max-w-[282px] lg:h-[64px]"
            onClick={() => {
              navigate(chinobiCtaPath);
            }}
          >
            <img
              src={chinobiAvatar}
              alt="Chinobi"
              className="h-6 w-6 shrink-0 rounded-full bg-[#F0EEDD] object-contain object-top sm:h-8 sm:w-8"
            />
            <span>Speak with Chinobi</span>
            <VideoCallIcon
              aria-hidden="true"
              className="text-white/90 h-3.5 w-3.5 sm:h-4 sm:w-4"
            />
          </Button>

          {/* Schedule a Demo text link with underline */}
          <Link
            to={ROUTES.CONTACT}
            id="schedule-demo-link"
            className="group inline-flex w-full flex-col items-center text-xs font-medium text-foreground transition-colors hover:text-black sm:w-auto sm:text-base md:max-w-[282px]"
          >
            <span>Schedule a Demo</span>
            <span className="mt-2 h-[1.5px] w-full bg-foreground transition-colors group-hover:bg-primary/80" />
          </Link>
        </div>
      </div>
    </section>
  );
}
